import { logger } from "../../utils/logger";
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Block as BlockIcon,
  TransferWithinAStation as TransferIcon,
  Info as InfoIcon,
  Send as SendIcon,
} from '@mui/icons-material';

const GDPRRights = () => {
  const [selectedRight, setSelectedRight] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    requestDetails: '',
    dataType: '',
  });
  const [showDialog, setShowDialog] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const gdprRights = [
    {
      id: 'access',
      title: 'Right to Access',
      description: 'Request a copy of all personal data we hold about you',
      icon: <PersonIcon />,
      details: 'You have the right to obtain confirmation as to whether or not personal data concerning you is being processed, and access to that data.'
    },
    {
      id: 'rectification',
      title: 'Right to Rectification',
      description: 'Request correction of inaccurate or incomplete personal data',
      icon: <EditIcon />,
      details: 'You have the right to have inaccurate personal data rectified, or incomplete personal data completed.'
    },
    {
      id: 'erasure',
      title: 'Right to Erasure',
      description: 'Request deletion of your personal data ("right to be forgotten")',
      icon: <DeleteIcon />,
      details: 'You have the right to obtain the erasure of personal data concerning you without undue delay.'
    },
    {
      id: 'restriction',
      title: 'Right to Restriction',
      description: 'Request limitation of how we process your personal data',
      icon: <BlockIcon />,
      details: 'You have the right to obtain restriction of processing of your personal data.'
    },
    {
      id: 'portability',
      title: 'Right to Data Portability',
      description: 'Request your data in a structured, commonly used format',
      icon: <TransferIcon />,
      details: 'You have the right to receive the personal data concerning you in a structured, commonly used format.'
    },
    {
      id: 'objection',
      title: 'Right to Object',
      description: 'Object to processing of your personal data',
      icon: <InfoIcon />,
      details: 'You have the right to object to processing of personal data concerning you.'
    }
  ];

  const handleRightSelect = (rightId) => {
    setSelectedRight(rightId);
    setShowDialog(true);
  };

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async () => {
    // Here you would typically send the request to your backend
    logger.log('GDPR Request:', {
      right: selectedRight,
      ...formData
    });

    // Simulate API call
    setTimeout(() => {
      setSubmitted(true);
      setShowDialog(false);
    }, 1000);
  };

  const selectedRightData = gdprRights.find(right => right.id === selectedRight);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={2} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <PersonIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          <Typography variant="h3" component="h1" gutterBottom>
            GDPR Data Subject Rights
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Exercise Your Rights Under the General Data Protection Regulation
          </Typography>
        </Box>

        <Typography variant="body1" paragraph>
          Under the General Data Protection Regulation (GDPR), you have several rights regarding your personal data. GoTruTax is committed to respecting and upholding these rights. Below, you can find information about each right and submit requests to exercise them. For any privacy-related concerns, contact <a href="mailto:support@gotrutax.com">support@gotrutax.com</a>.
        </Typography>

        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="body2">
            <strong>Important:</strong> To protect your privacy and security, we may need to verify your identity
            before processing your request.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          {gdprRights.map((right) => (
            <Grid item xs={12} md={6} key={right.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ color: 'primary.main', mr: 1 }}>
                      {right.icon}
                    </Box>
                    <Typography variant="h6" component="h3">
                      {right.title}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    {right.description}
                  </Typography>
                  <Typography variant="body2">
                    {right.details}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleRightSelect(right.id)}
                    startIcon={<SendIcon />}
                  >
                    Submit Request
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Timeline section removed for compliance */}

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom>
          What Information Do We Collect?
        </Typography>
        <Typography variant="body1" paragraph>
          To provide our tax calculation and compliance services, we may collect the following types of personal data:
        </Typography>
        <List>
          <ListItem>
            <ListItemText primary="Identity Information: Name, email address, phone number" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Financial Information: Tax IDs, business registration details, transaction data" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Usage Data: How you interact with our website and services" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Technical Data: IP address, browser type, device information" />
          </ListItem>
          <ListItem>
            <ListItemText primary="Communication Data: Messages, support requests, feedback" />
          </ListItem>
        </List>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h5" gutterBottom>
          Contact Information
        </Typography>
        <Typography variant="body1" paragraph>
          If you have questions about your rights or need assistance with your request, please contact:
        </Typography>
        <Typography variant="body1">
          Email: support@gotrutax.com<br />
          Address: [Your Business Address]<br />
          Phone: [Your Phone Number]<br />
        </Typography>

        {submitted && (
          <Alert severity="success" sx={{ mt: 3 }}>
            Your GDPR request has been submitted successfully. We will contact you with updates.
          </Alert>
        )}
      </Paper>

      {/* Request Dialog */}
      <Dialog open={showDialog} onClose={() => setShowDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedRightData?.title} Request
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            {selectedRightData?.details}
          </Typography>

          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                value={formData.fullName}
                onChange={handleInputChange('fullName')}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Data Type (Optional)</InputLabel>
                <Select
                  value={formData.dataType}
                  onChange={handleInputChange('dataType')}
                  label="Data Type (Optional)"
                >
                  <MenuItem value="">All Data</MenuItem>
                  <MenuItem value="personal">Personal Information</MenuItem>
                  <MenuItem value="financial">Financial Data</MenuItem>
                  <MenuItem value="usage">Usage Analytics</MenuItem>
                  <MenuItem value="communication">Communication Records</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Additional Details (Optional)"
                multiline
                rows={4}
                value={formData.requestDetails}
                onChange={handleInputChange('requestDetails')}
                placeholder="Please provide any additional information that may help us process your request..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={!formData.fullName || !formData.email}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GDPRRights;
